import { useReactFlow, NodeProps } from '@xyflow/react'
import DOMPurify from 'dompurify'
import { 
  Image as ImageIcon, 
  Sparkles, 
  RefreshCw, 
  Save, 
  Copy, 
  Download, 
  Trash, 
  HelpCircle,
  Wand2,
  Settings,
  ImagePlus,
  Palette,
  Code,
} from 'lucide-react'
import React, { useState, useEffect, useRef, memo, useMemo } from 'react'
import Markdown from 'react-markdown' 
import { useParams } from 'react-router'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useUser } from '@/context/UserContext'
import useDataSettings from '@/hooks/useDataSettings'
import styles from '@/styles/NodeImageGenerator.module.css'
import {
  AICommand,
  AICommandType,
  AICommandStatus,
  createAICommand,
  isCommandComplete,
  isCommandError,
  isCommandInProgress,
  createNodeCommandPayload,
  useAICommandState,
} from '@/utils/aiCommandUtils'
import NodeBaseActions from '../base/base-actions/NodeBaseActions'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'

interface NodeImageGeneratorData {
  label?: string
  isMinimized?: boolean
  isLocked?: boolean
  statusMessage?: string
  prompt?: string
  model?: string
  size?: string
  quality?: string
  style?: string
  generatedImage?: {
    url: string
    filename: string
    id?: string
    contentType?: string
  }
  contentMarkdown?: string
  userId?: string
  projectId?: string
  documentRequest?: {
    document: string
    processing: boolean
    processedAt: string
    timestamp: number
    'sysagent-cmd': string
    'sysagent-response': string
    [key: string]: any
  }
  assignedEmails?: string[] | string
  dataSettings?: any
}

// Available models from OpenAI
const IMAGE_MODELS = [
  { id: 'dall-e-3', name: 'DALL-E 3 (Latest)', description: 'Most advanced model with highest quality results' },
  { id: 'dall-e-2', name: 'DALL-E 2', description: 'Previous generation model with faster generation' }
]

// Size options based on model
const SIZE_OPTIONS = {
  'dall-e-3': [
    { id: '1024x1024', name: 'Square (1024×1024)' },
    { id: '1024x1792', name: 'Portrait (1024×1792)' },
    { id: '1792x1024', name: 'Landscape (1792×1024)' }
  ],
  'dall-e-2': [
    { id: '256x256', name: 'Small (256×256)' },
    { id: '512x512', name: 'Medium (512×512)' },
    { id: '1024x1024', name: 'Large (1024×1024)' }
  ]
}

// Quality options (DALL-E 3 only)
const QUALITY_OPTIONS = [
  { id: 'standard', name: 'Standard', description: 'Default quality' },
  { id: 'hd', name: 'HD', description: 'Higher quality with more detail' }
]

// Style options (DALL-E 3 only)
const STYLE_OPTIONS = [
  { id: 'vivid', name: 'Vivid', description: 'Hyper-real and dramatic' },
  { id: 'natural', name: 'Natural', description: 'More natural and photographic' }
]

// Example prompts for inspiration
const EXAMPLE_PROMPTS = [
  "A futuristic cityscape with flying cars and neon lights at sunset",
  "A detailed portrait of a cybernetic character with glowing elements",
  "An ancient temple hidden in a lush jungle with mystical fog",
  "A photorealistic image of a new technology device concept",
  "A fantasy landscape with floating islands and a waterfall"
]

const NodeImageGenerator: React.FC<NodeProps & { data: NodeImageGeneratorData }> = (props) => {
  const { id, data, selected } = props
  const { projectId } = useParams()
  const { updateNodeData } = useReactFlow()
  const user = useUser()
  
  // State management
  const [prompt, setPrompt] = useState('')
  const [model, setModel] = useState(data.model || 'dall-e-3')
  const [size, setSize] = useState(data.size || '1024x1024')
  const [quality, setQuality] = useState(data.quality || 'standard')
  const [style, setStyle] = useState(data.style || 'vivid')
  const [statusMessage, setStatusMessage] = useState(data.statusMessage || '')
  const [isLocked, setIsLocked] = useState(data.isLocked || false)
  const [isMinimized, setIsMinimized] = useState(data.isMinimized || false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editingTitleValue, setEditingTitleValue] = useState(data.label || '')
  const [activeTab, setActiveTab] = useState<'generation' | 'result'>('generation')
  const [showHelpDialog, setShowHelpDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [showImageOptions, setShowImageOptions] = useState(false)
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
    Array.isArray(data.assignedEmails) ? data.assignedEmails : 
    (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : [])
  )
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)

  const titleInputRef = useRef<HTMLInputElement>(null)
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Monitor command state
  const imageGenerationState = useAICommandState(data.documentRequest, AICommandType.IMAGE_GENERATE)
  
  const currentStatus = isGenerating 
    ? 'loading'
    : data.documentRequest && isCommandComplete(data.documentRequest, AICommandType.IMAGE_GENERATE)
    ? 'success'
    : data.documentRequest && isCommandError(data.documentRequest, AICommandType.IMAGE_GENERATE)
    ? 'error'
    : ''

  useEffect((() => {
    setPrompt(data.prompt)
  }), [data.prompt])

  useEffect(() => {
    if (projectId) {
      syncNodeData({ projectId })
    } else if (data.projectId) {
      // Project ID already set
    } else {
      console.warn('No projectId available for NodeImageGenerator')
      setStatusMessage('Error: No project context available. Please set a project ID.')
      syncNodeData({ statusMessage: 'Error: No project context available. Please set a project ID.' })
    }
  }, [projectId, data.projectId])

  useEffect(() => {
    if (data.prompt !== undefined) setPrompt(data.prompt)
    if (data.model !== undefined) setModel(data.model)
    if (data.size !== undefined) setSize(data.size)
    if (data.quality !== undefined) setQuality(data.quality)
    if (data.style !== undefined) setStyle(data.style)
    if (data.statusMessage !== undefined) setStatusMessage(data.statusMessage)
    if (data.isMinimized !== undefined) setIsMinimized(data.isMinimized)
    if (data.isLocked !== undefined) setIsLocked(data.isLocked)
    if (data.label !== undefined) setEditingTitleValue(data.label)
    if (data.assignedEmails !== undefined)
      setAssignedEmails(Array.isArray(data.assignedEmails) ? data.assignedEmails : 
        (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : []))
  }, [])

  useEffect(() => {
    setIsGenerating(imageGenerationState.isProcessing)
  }, [imageGenerationState.isProcessing])

  // Sync node data to React Flow
  const syncNodeData = (newData: Partial<NodeImageGeneratorData>) => {
    const updatedData = { ...data, ...newData }
    updateNodeData(id, updatedData)
  }

  // Get available size options based on selected model
  const getAvailableSizeOptions = () => {
    return SIZE_OPTIONS[model] || SIZE_OPTIONS['dall-e-3']
  }

  // Handle model change and adjust size if needed
  const handleModelChange = (newModel: string) => {
    setModel(newModel)
    syncNodeData({ model: newModel })
    
    // Check if current size is valid for this model
    const availableSizes = SIZE_OPTIONS[newModel].map(s => s.id)
    if (!availableSizes.includes(size)) {
      const defaultSize = availableSizes[0]
      setSize(defaultSize)
      syncNodeData({ size: defaultSize })
    }
  }

  // Node action handlers
  const handleLock = () => {
    const newState = !isLocked
    setIsLocked(newState)
    syncNodeData({ isLocked: newState })
  }

  const handleMinimize = () => {
    const newState = !isMinimized
    setIsMinimized(newState)
    syncNodeData({ isMinimized: newState })
  }

  const confirmDelete = (item) => {
    setItemToDelete(item)
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirmed = () => {
    if (itemToDelete && (window as any).__flowDeleteNode) {
      (window as any).__flowDeleteNode(id)
    }
    setShowDeleteDialog(false)
    setItemToDelete(null)
  }

  const handleDuplicate = () => {
    if ((window as any).__flowDuplicateNode) {
      (window as any).__flowDuplicateNode(id)
    }
  }

  const handleTitleSave = () => {
    syncNodeData({ label: editingTitleValue })
    setIsEditingTitle(false)
  }

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave()
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false)
      setEditingTitleValue(data.label || '')
    }
  }

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  // Insert example prompt into the textarea
  const insertExamplePrompt = (examplePrompt: string) => {
    setPrompt(examplePrompt)
    syncNodeData({ prompt: examplePrompt })
    promptTextareaRef.current?.focus()
  }

  // Generate image based on current settings
  const handleGenerateImage = async () => {
    if (!prompt.trim()) {
      setStatusMessage('Error: Please enter a prompt for the image generation.')
      syncNodeData({ statusMessage: 'Error: Please enter a prompt for the image generation.' })
      return
    }

    const effectiveUserId = user?.id || user?.email
    if (!effectiveUserId) {
      setStatusMessage('Error: User information missing. Please log in.')
      syncNodeData({ statusMessage: 'Error: User information missing. Please log in.' })
      return
    }

    const currentProjectId = data.projectId || projectId
    if (!currentProjectId) {
      setStatusMessage('Error: No project ID available. Please reload the page.')
      syncNodeData({ statusMessage: 'Error: No project ID available. Please reload the page.' })
      return
    }

    setStatusMessage('Preparing to generate image...')
    syncNodeData({ statusMessage: 'Preparing to generate image...' })

    try {
      // Prepare the command to be processed by ImageGeneratorProcessor
      const documentRequest = createAICommand(
        AICommandType.IMAGEGENERATOR_PROCESS,
        AICommandStatus.STARTED,
        createNodeCommandPayload(id, {
          prompt,
          model,
          size,
          quality,
          style,
          userId: effectiveUserId,
          projectId: currentProjectId,
          document: 'image-generation'
        })
      )

      syncNodeData({
        documentRequest,
        prompt,
        model,
        size,
        quality,
        style,
        userId: effectiveUserId
      })

      setActiveTab('result')
    } catch (error) {
      console.error('Error preparing image generation:', error)
      setStatusMessage(`Error: ${error.message || 'Unknown error'}`)
      syncNodeData({ statusMessage: `Error: ${error.message || 'Unknown error'}` })
    }
  }

  // Copy image URL to clipboard
  const copyImageUrl = () => {
    if (data.generatedImage?.url) {
      navigator.clipboard.writeText(data.generatedImage.url)
      setStatusMessage('Image URL copied to clipboard')
      syncNodeData({ statusMessage: 'Image URL copied to clipboard' })
      setTimeout(() => {
        setStatusMessage('')
        syncNodeData({ statusMessage: '' })
      }, 3000)
    }
  }

  // Copy image markdown to clipboard
  const copyImageMarkdown = () => {
    if (data.generatedImage?.url) {
      const markdown = `![${data.generatedImage.filename || 'Generated Image'}](${data.generatedImage.url})`
      navigator.clipboard.writeText(markdown)
      setStatusMessage('Image markdown copied to clipboard')
      syncNodeData({ statusMessage: 'Image markdown copied to clipboard' })
      setTimeout(() => {
        setStatusMessage('')
        syncNodeData({ statusMessage: '' })
      }, 3000)
    }
  }

  // Handle image download
  const downloadImage = () => {
    if (data.generatedImage?.url) {
      const link = document.createElement('a')
      link.href = data.generatedImage.url
      link.download = data.generatedImage.filename || 'generated-image.png'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  // Process the image generation result when the command completes
  useEffect(() => {
    if (!data.documentRequest) return

    if (isCommandComplete(data.documentRequest, AICommandType.IMAGE_GENERATE)) {
      setStatusMessage('Image generated successfully')
      if (data.documentRequest.image) {
        setTimeout(() => {
          syncNodeData({
            generatedImage: data.documentRequest.image,
            contentMarkdown: data.documentRequest.formattedContent || `![${data.documentRequest.image.filename}](${data.documentRequest.image.url})`,
            statusMessage: 'Image generated successfully',
            documentRequest: undefined,
          })
        }, 100)
        setActiveTab('result')
      }
    } else if (isCommandError(data.documentRequest, AICommandType.IMAGE_GENERATE)) {
      const errorMsg = data.documentRequest.error || 'Unknown error'
      setStatusMessage(`Error generating image: ${errorMsg}`)
      setTimeout(() => {
        syncNodeData({ 
          statusMessage: `Error generating image: ${errorMsg}`,
          documentRequest: undefined,
        })
      }, 100)
    }
  }, [data.documentRequest])

  // Validate if the selected model supports the selected quality and style
  const isAdvancedOptionSupported = () => {
    return model === 'dall-e-3'
  }

  // Compute status for NodeBaseContainer using useMemo
  const nodeStatus = useMemo(() => {
    if (!data?.statusMessage) return "initial";
    const lowerMessage = data.statusMessage.toLowerCase();
    if (lowerMessage.includes("loading")) return "loading";
    if (lowerMessage.includes("error")) return "error";
    if (lowerMessage.includes("complete") || lowerMessage.includes("successful")) return "success";
    return "initial";
  }, [data?.statusMessage]);

  // --- Data Settings logic (outgoing only) ---
  // Only expose outgoing data (no inputLinks)
  const outgoingFields = {
    prompt: {
      value: prompt,
      enabled: true,
      label: 'Image Prompt',
      description: 'The prompt used for image generation',
      permissions: { readable: true, writable: false },
    },
    generatedImageUrl: {
      value: data.generatedImage?.url || '',
      enabled: !!data.generatedImage?.url,
      label: 'Generated Image URL',
      description: 'URL of the generated image',
      permissions: { readable: true, writable: false },
    },
    generatedImageFilename: {
      value: data.generatedImage?.filename || '',
      enabled: !!data.generatedImage?.filename,
      label: 'Image Filename',
      description: 'Filename of the generated image',
      permissions: { readable: true, writable: false },
    },
    contentMarkdown: {
      value: data.contentMarkdown || '',
      enabled: !!data.contentMarkdown,
      label: 'Image Markdown',
      description: 'Markdown representation of the image',
      permissions: { readable: true, writable: false },
    },
    imageSettings: {
      value: JSON.stringify({ model, size, quality, style }),
      enabled: true,
      label: 'Image Settings',
      description: 'Generation settings (model, size, quality, style)',
      permissions: { readable: true, writable: false },
    },
  }

  // Use useDataSettings for outgoing/exposed data only
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleOpenDataSettingsDialog,
    prepareExposedDataFields,
  } = useDataSettings(
    id,
    data,
    (newData) => syncNodeData(newData),
    false, // showDebugInfo
    '', // context not relevant here
    new Map(), // sections not relevant
    '', // contentMarkdown not relevant
    setIsDataSettingsDialogVisible
  )

  // Always keep outgoing/exposedData up to date
  useEffect(() => {
    // Only outgoing/exposedData, no inputLinks
    const newDataSettings = {
      ...dataSettings,
      inputLinks: [],
      exposedData: outgoingFields,
      availableNodes: [],
      outputLinks: dataSettings?.outputLinks || [],
      autoSync: false,
    }
    if (JSON.stringify(dataSettings?.exposedData) !== JSON.stringify(outgoingFields)) {
      setDataSettings(newDataSettings)
      syncNodeData({ dataSettings: newDataSettings })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prompt,
    data.generatedImage,
    data.contentMarkdown,
    model,
    size,
    quality,
    style,
  ])

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={!isLocked}
      isProcessing={isGenerating} 
      status={nodeStatus}
      nodeTitle={editingTitleValue || data.label || 'Image Generator'}
      onTitleChange={(newTitle) => {
        setEditingTitleValue(newTitle)
        syncNodeData({ label: newTitle })
      }}
      assignedEmails={Array.isArray(assignedEmails) ? assignedEmails : (assignedEmails ? assignedEmails.trim().split(/\s+/).filter(Boolean) : [])}
      onAssignUsersClick={() => {
        const emailsStr = window.prompt('Enter emails separated by space', Array.isArray(assignedEmails) ? assignedEmails.join(' ') : (assignedEmails || ''))
        if (emailsStr !== null) {
          const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
          setAssignedEmails(emailsArr)
          syncNodeData({ assignedEmails: emailsArr })
        }
      }}
      selected={selected} // ✅ CRITICAL: Add the missing selected prop!
    >
      <div className="flex flex-col h-full w-full bg-white rounded-lg shadow">
        <div className="flex justify-between items-center px-2 py-3">
          <NodeBaseActions
            nodeId={id}
            onDelete={confirmDelete}
            onDuplicate={handleDuplicate}
            onLock={handleLock}
            onUnlock={isLocked}
            onMinimize={handleMinimize}
            onMaximize={isMinimized}
          />
          <div className="flex items-center space-x-2">
            <button
              className="px-2 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              onClick={() => setShowHelpDialog(true)}
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <div className="flex flex-col flex-grow px-2 pb-2 space-y-2">
            <Tabs 
              defaultValue="generation" 
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as 'generation' | 'result')}
              className="w-full"
            >
              <TabsList className="grid grid-cols-2">
                <TabsTrigger value="generation">Image Generation</TabsTrigger>
                <TabsTrigger value="result">Result</TabsTrigger>
              </TabsList>

              <TabsContent value="generation" className="border-none p-0 mt-2">
                <div className="flex flex-col space-y-4">
                  <div className="border rounded-md p-3">
                    <label className="block text-sm font-medium mb-1">Image Prompt:</label>
                    <Textarea
                      ref={promptTextareaRef}
                      placeholder="Describe the image you want to generate in detail..."
                      value={prompt}
                      onChange={(e) => {
                        setPrompt(e.target.value)
                      }}
                      onBlur={() => {
                        syncNodeData({ prompt })
                      }}
                      className="min-h-[100px] nodrag" 
                      disabled={isLocked || isGenerating}
                    />
                    
                    <div className="mt-3">
                      <label className="block text-sm font-medium mb-1">Example Prompts:</label>
                      <ScrollArea className="h-[80px]">
                        <div className="flex flex-wrap gap-2">
                          {EXAMPLE_PROMPTS.map((exPrompt, index) => (
                            <Button 
                              key={index} 
                              variant="outline" 
                              size="sm"
                              onClick={() => insertExamplePrompt(exPrompt)}
                              disabled={isLocked || isGenerating}
                            >
                              <Sparkles className="h-3 w-3 mr-1" />
                              {exPrompt.substring(0, 20)}...
                            </Button>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  </div>
                  
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowImageOptions(!showImageOptions)}
                      className="mb-2"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      {showImageOptions ? 'Hide Options' : 'Show Options'}
                    </Button>
                    
                    {showImageOptions && (
                      <div className="border rounded-md p-3 space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">Model:</label>
                          <Select
                            value={model}
                            onValueChange={handleModelChange}
                            disabled={isLocked || isGenerating}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select model" />
                            </SelectTrigger>
                            <SelectContent>
                              {IMAGE_MODELS.map(modelOption => (
                                <SelectItem key={modelOption.id} value={modelOption.id}>
                                  {modelOption.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">
                            {IMAGE_MODELS.find(m => m.id === model)?.description}
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1">Size:</label>
                          <Select
                            value={size}
                            onValueChange={(value) => {
                              setSize(value)
                              syncNodeData({ size: value })
                            }}
                            disabled={isLocked || isGenerating}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select size" />
                            </SelectTrigger>
                            <SelectContent>
                              {getAvailableSizeOptions().map(sizeOption => (
                                <SelectItem key={sizeOption.id} value={sizeOption.id}>
                                  {sizeOption.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {isAdvancedOptionSupported() && (
                          <>
                            <div>
                              <label className="block text-sm font-medium mb-1">Quality:</label>
                              <Select
                                value={quality}
                                onValueChange={(value) => {
                                  setQuality(value)
                                  syncNodeData({ quality: value })
                                }}
                                disabled={isLocked || isGenerating || model !== 'dall-e-3'}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select quality" />
                                </SelectTrigger>
                                <SelectContent>
                                  {QUALITY_OPTIONS.map(qualityOption => (
                                    <SelectItem key={qualityOption.id} value={qualityOption.id}>
                                      {qualityOption.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-gray-500 mt-1">
                                {QUALITY_OPTIONS.find(q => q.id === quality)?.description}
                              </p>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-1">Style:</label>
                              <Select
                                value={style}
                                onValueChange={(value) => {
                                  setStyle(value)
                                  syncNodeData({ style: value })
                                }}
                                disabled={isLocked || isGenerating || model !== 'dall-e-3'}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select style" />
                                </SelectTrigger>
                                <SelectContent>
                                  {STYLE_OPTIONS.map(styleOption => (
                                    <SelectItem key={styleOption.id} value={styleOption.id}>
                                      {styleOption.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-gray-500 mt-1">
                                {STYLE_OPTIONS.find(s => s.id === style)?.description}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex">
                    <Button
                      className="flex-1"
                      onClick={handleGenerateImage}
                      disabled={isLocked || isGenerating || !prompt?.trim()}
                    >
                      {isGenerating ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Wand2 className="h-4 w-4 mr-2" />
                          Generate Image
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="result" className="border-none p-0 mt-2">
                <div className="flex flex-col space-y-4">
                  <div className="border rounded-md p-3">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">Generated Image</h3>
                      {data.generatedImage?.url && (
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={copyImageUrl}>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy URL
                          </Button>
                          <Button variant="outline" size="sm" onClick={copyImageMarkdown}>
                            <Code className="h-4 w-4 mr-1" />
                            Copy MD
                          </Button>
                          <Button variant="outline" size="sm" onClick={downloadImage}>
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {data.generatedImage?.url ? (
                      <div className="flex flex-col items-center justify-center">
                        <div className="relative border rounded-md overflow-hidden">
                          <img 
                            src={data.generatedImage.url} 
                            alt="Generated" 
                            className="max-w-full max-h-[400px] object-contain"
                          />
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          <div><strong>Prompt:</strong> {data.prompt}</div>
                          <div className="mt-1"><strong>Settings:</strong> {data.model} • {data.size} • {model === 'dall-e-3' ? `${data.quality} quality • ${data.style} style` : ''}</div>
                        </div>
                      </div>
                    ) : isGenerating ? (
                      <div className="h-[300px] flex flex-col items-center justify-center text-center">
                        <div className="animate-spin mb-4">
                          <RefreshCw className="h-8 w-8 text-gray-400" />
                        </div>
                        <p>Generating your image...</p>
                        <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
                      </div>
                    ) : (
                      <div className="h-[300px] flex flex-col items-center justify-center text-center">
                        <ImageIcon className="h-16 w-16 text-gray-300 mb-4" />
                        <p className="text-gray-500">No image generated yet</p>
                        <p className="text-sm text-gray-400 mt-2">Go to the Image Generation tab to create an image</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {data.contentMarkdown && (
                  <div className="border rounded-md p-3 mt-2">
                    <h3 className="font-medium mb-2">Markdown</h3>
                    <div className="bg-gray-50 p-2 rounded-md text-sm font-mono break-all">
                      {data.contentMarkdown}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
            
            {statusMessage && !isGenerating && (
              <div className="text-sm text-gray-600">{statusMessage}</div>
            )}
          </div>
        )}

        <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Image Generator Help</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p><strong>Image Generator</strong> allows you to create custom images with AI for your projects.</p>
              <div className="space-y-2">
                <h4 className="font-medium">How to use:</h4>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Enter a detailed prompt describing the image you want</li>
                  <li>Optionally modify the generation settings</li>
                  <li>Click "Generate Image" to create your image</li>
                  <li>View, download, or copy the resulting image</li>
                </ol>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">Tips for better results:</h4>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Be specific and detailed in your prompts</li>
                  <li>Mention artistic style, lighting, perspective, etc.</li>
                  <li>Try different models and settings for various results</li>
                  <li>DALL-E 3 provides higher quality but may take longer</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">Available settings:</h4>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Model:</strong> DALL-E 3 (highest quality) or DALL-E 2 (faster)</li>
                  <li><strong>Size:</strong> Different aspect ratios and dimensions</li>
                  <li><strong>Quality:</strong> Standard or HD (DALL-E 3 only)</li>
                  <li><strong>Style:</strong> Vivid (dramatic) or Natural (photographic)</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowHelpDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this Image Generator node? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirmed}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </NodeBaseContainer>
  )
}

export default memo(NodeImageGenerator)